// src/components/layout/PlacementsSection.jsx
import React, { useEffect, useRef, useState } from 'react';
import '../../styles/placements.css';

// --- HELPER: Number Counter Component ---
const Counter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  // 1. SAFETY GUARD: If 'end' is undefined or null, default to "0"
  const safeValue = end ? end.toString() : "0";

  useEffect(() => {
    // Parse the input (Using safeValue instead of end)
    const numericValue = parseInt(safeValue.replace(/[^0-9]/g, ''), 10);
    
    if (isNaN(numericValue)) return; 

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = currentTime - startTime;
      
      if (progress < duration) {
        const nextCount = Math.floor(numericValue * (progress / duration));
        setCount(nextCount);
        countRef.current = requestAnimationFrame(animate);
      } else {
        setCount(numericValue); 
      }
    };

    countRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(countRef.current);
  }, [safeValue, duration]);

  // Reconstruct suffix using safeValue
  const suffix = safeValue.replace(/[0-9]/g, '');
  
  return <>{count}{suffix}</>;
};

// ... Rest of the PlacementsSection component remains the same

const PlacementsSection = ({ college }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  if (!college || !college.placements) return null;

  const { highestPackage, recruitersIndustries, gloriousAlumni, placementRecords, recruitersImage } = college.placements;
  const totalOffers = "15500+"; 

  return (
    <section 
      ref={sectionRef} 
      className={`placements-section ${isVisible ? 'is-visible' : ''}`}
    >
      <div className="placements-container">
        
        {/* --- Header --- */}
        <div className="placements-header">
          <span className="placements-label">Career Success</span>
          <h2 className="placements-title">Placements & Impact</h2>
          <p className="placements-subtitle">
            Building careers with record-breaking opportunities at {college.name}
          </p>
        </div>
        
        {/* --- NEW PREMIUM STATS BANNER --- */}
        <div className="placements-banner">
          
          {/* Left Column */}
          <div className="banner-col col-left">
            <div className="stat-block" style={{ transitionDelay: '200ms' }}>
              <h3 className="stat-number">
                {isVisible ? <Counter end={recruitersIndustries} /> : "0"}
              </h3>
              <p className="stat-label">Companies</p>
            </div>
            <div className="stat-block" style={{ transitionDelay: '300ms' }}>
              <h3 className="stat-number">
                {isVisible ? <Counter end={gloriousAlumni} /> : "0"}
              </h3>
              <p className="stat-label">Alumni</p>
            </div>
          </div>

          {/* Middle Column */}
          <div className="banner-col col-middle">
            <div className="stat-block main-stat" style={{ transitionDelay: '400ms' }}>
              <h3 className="stat-number">
                {/* placementRecords might be "100%", passing it directly works with our parser */}
                {isVisible ? <Counter end={placementRecords} /> : "0"}
              </h3>
              <p className="stat-label">Average Placement Records</p>
            </div>
          </div>

          {/* Right Column */}
          <div className="banner-col col-right">
             <div className="stat-block" style={{ transitionDelay: '500ms' }}>
              <h3 className="stat-number">
                {isVisible ? <Counter end={totalOffers} /> : "0"}
              </h3>
              <p className="stat-label">Placement Offers</p>
            </div>
            <div className="stat-block" style={{ transitionDelay: '600ms' }}>
              <h3 className="stat-number">{highestPackage}</h3>
              <p className="stat-label">Highest Package</p>
            </div>
          </div>

        </div>

        {/* --- Recruiters Image Section --- */}
        <div className="recruiters-wrapper">
          <div className="recruiters-header">
            <h3>Our Top Recruiters</h3>
            <div className="divider"></div>
          </div>
          <div className="recruiters-image-box">
            <img 
              src={recruitersImage || "https://via.placeholder.com/1200x400?text=Top+Recruiters+Logos"} 
              alt="Top Recruiters" 
              className="recruiters-image"
            />
          </div>
        </div>

      </div>
    </section>
  );
};

export default PlacementsSection;